/**
 * Rich school content sections (overview, academics, facilities, FAQs).
 *
 * What: Renders unique-per-school heritage sections + FAQs from data passed in by parent.
 * Why: Substantial unique content per school page so Google indexes each /schools/{slug}
 *      URL as distinct rather than deduping as "very similar entries".
 * When: Update when heritage section rendering changes.
 */
import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import type { SchoolRichHeritage } from '@shared/schools/schoolRichHeritage.js';
import LandingSectionShell from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';

/**
 * Anchor id for the nth heritage prose section.
 *
 * Exported because the page's contents nav links to these sections. Two call
 * sites composing the same string by hand is how one of them silently stops
 * matching.
 *
 * Not a scroll-reveal key: this prose paints on arrival rather than animating
 * in — see the note above `support` in `sectionRevealMap`.
 */
export function schoolHeritageSectionId(index: number): string {
  return `school-heritage-${index}`;
}

/** Anchor id for the school-specific FAQ block. */
export const SCHOOL_HERITAGE_FAQ_ID = 'school-heritage-faq';

interface SchoolRichContentProps {
  data: SchoolRichHeritage | null;
}

function SchoolRichContent({ data }: SchoolRichContentProps) {
  if (!data || (data.sections.length === 0 && data.faqs.length === 0)) {
    return null;
  }

  return (
    <Stack spacing={{ xs: 4, sm: 5, md: 6 }}>
      {/* Content sections */}
      {data.sections.map((section, idx) => (
        <LandingSectionShell
          key={idx}
          id={schoolHeritageSectionId(idx)}
          ariaLabelledby={`${schoolHeritageSectionId(idx)}-title`}
          // Spacing between these sections comes from the surrounding stack.
          mb={0}
          sx={{ width: '100%' }}
        >
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${colors.border}`,
              borderRadius: radii.md,
              backgroundColor: colors.backgroundAlt,
              boxShadow: shadows.card,
              p: { xs: 2.5, sm: 3, md: 4 },
            }}
          >
            <Stack spacing={{ xs: 2, sm: 2.5 }}>
              <Typography
                id={`${schoolHeritageSectionId(idx)}-title`}
                component="h2"
                sx={{
                  fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.625rem' },
                  fontWeight: 800,
                  color: colors.ink,
                  lineHeight: 1.25,
                  letterSpacing: '-0.02em',
                }}
              >
                {section.heading}
              </Typography>
              {section.body.split('\n').map((paragraph, pIdx) => (
                <Typography
                  key={pIdx}
                  sx={{
                    fontSize: { xs: '0.9375rem', sm: '1rem' },
                    color: colors.inkMuted,
                    lineHeight: 1.65,
                  }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Stack>
          </Paper>
        </LandingSectionShell>
      ))}

      {/* FAQs */}
      {data.faqs.length > 0 && (
        <LandingSectionShell
          id={SCHOOL_HERITAGE_FAQ_ID}
          ariaLabelledby={`${SCHOOL_HERITAGE_FAQ_ID}-title`}
          mb={0}
        >
          <Stack spacing={{ xs: 2, sm: 2.5 }}>
            <Typography
              id={`${SCHOOL_HERITAGE_FAQ_ID}-title`}
              component="h2"
              sx={{
                fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.625rem' },
                fontWeight: 800,
                color: colors.ink,
                lineHeight: 1.25,
                letterSpacing: '-0.02em',
              }}
            >
              Frequently Asked Questions about {data.displayName}
            </Typography>
            {data.faqs.map((faq, idx) => (
              <Accordion
                key={idx}
                disableGutters
                elevation={0}
                sx={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: `${radii.sm} !important`,
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon sx={{ color: colors.inkMuted }} />
                  }
                  sx={{
                    backgroundColor: colors.backgroundAlt,
                    px: { xs: 2, sm: 2.5 },
                    py: 0.5,
                    '& .MuiAccordionSummary-content': { my: 1.5 },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '0.9375rem', sm: '1rem' },
                      fontWeight: 700,
                      color: colors.ink,
                      lineHeight: 1.4,
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: 2,
                    backgroundColor: colors.background,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                      color: colors.inkMuted,
                      lineHeight: 1.65,
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </LandingSectionShell>
      )}
    </Stack>
  );
}

export default SchoolRichContent;
